import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { FilePreview } from '../components/FilePreview';
import { CompanyInfo } from '../components/recruitment/CompanyInfo';
import { RoleDetails } from '../components/recruitment/RoleDetails';
import { CookieConsent } from '../components/recruitment/CookieConsent';
import { openCookiePreferences, fireApplicationCompleted } from '../data/legal';
import { HardHat, MapPin, CheckCircle2, ArrowLeft, ArrowRight, Save, ChevronRight, User, Users, ClipboardCheck, Briefcase, TrendingUp, Shield, ShieldCheck, Share2, Copy, Mail, Image as ImageIcon, Download, Instagram } from 'lucide-react';

const TRACK_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'li_fat_id'];
function captureTracking() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const t = {};
    TRACK_KEYS.forEach(k => { const v = sp.get(k); if (v) t[k] = v; });
    t.landing_page = window.location.pathname + window.location.search;
    t.referrer = document.referrer || '';
    t.first_seen = new Date().toISOString();
    const prev = JSON.parse(sessionStorage.getItem('mc_track') || '{}');
    const merged = { ...t, ...prev };
    sessionStorage.setItem('mc_track', JSON.stringify(merged));
    return merged;
  } catch { return {}; }
}

function ShareBar({ url, title, text, label, image }) {
  const [copied, setCopied] = useState('');
  const [sharing, setSharing] = useState(false);
  const enc = encodeURIComponent;
  const postText = `${text} ${url}`;
  const hasNative = typeof navigator !== 'undefined' && !!navigator.share;
  const shareNative = async () => {
    try { await navigator.share({ title, text, url }); } catch (e) {}
  };
  const shareImage = async () => {
    setSharing(true);
    try {
      const res = await fetch(image);
      const blob = await res.blob();
      const file = new File([blob], 'metal-covering-assume.jpg', { type: blob.type || 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: postText });
      } else {
        window.open(image, '_blank');
      }
    } catch (e) { window.open(image, '_blank'); }
    setSharing(false);
  };
  const copy = async (what, value) => { try { await navigator.clipboard.writeText(value); setCopied(what); setTimeout(() => setCopied(''), 2000); } catch (e) {} };
  const link = 'inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-slate-50 cursor-pointer';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="share-bar">
      <p className="text-sm font-semibold text-slate-700">{label || 'Condividi le posizioni aperte'}</p>

      {hasNative && (
        <div>
          <p className="text-xs text-slate-500 mb-1.5">Consigliato dal telefono</p>
          <button type="button" className="inline-flex items-center gap-2 rounded-md bg-[#1a4b8c] text-white px-4 py-2 text-sm font-semibold hover:bg-[#163d73] transition-colors" onClick={shareNative} data-testid="share-native">
            <Share2 size={16} /> Condividi (Instagram, Facebook, WhatsApp…)
          </button>
        </div>
      )}

      <div>
        <p className="text-xs text-slate-500 mb-1.5">Condividi come immagine {image ? '(ideale per Instagram e storie)' : ''}</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={link + ' text-pink-700 border-pink-200'} onClick={shareImage} disabled={sharing} data-testid="share-image">
            <Instagram size={14} /> {sharing ? 'Preparo…' : 'Condividi immagine / storia'}
          </button>
          <a className={link + ' border-slate-300'} href={image} download="metal-covering-assume.jpg" data-testid="download-image"><Download size={14} /> Scarica immagine</a>
          <button type="button" className={link + ' border-slate-300'} onClick={() => copy('post', postText)} data-testid="share-copy-post"><Copy size={14} /> {copied === 'post' ? 'Testo copiato!' : 'Copia testo post'}</button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">Su Instagram non si può pubblicare un link automaticamente: condividi l'immagine (o scaricala) e incolla il testo copiato nella descrizione/storia.</p>
      </div>

      <div>
        <p className="text-xs text-slate-500 mb-1.5">Pubblica come post (da computer)</p>
        <div className="flex flex-wrap gap-2">
          <a className={link + ' text-blue-700 border-blue-200'} href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="share-facebook">Facebook</a>
          <a className={link + ' text-sky-700 border-sky-200'} href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="share-linkedin">LinkedIn</a>
          <a className={link + ' text-slate-800 border-slate-300'} href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="share-x">X / Twitter</a>
        </div>
      </div>

      <div>
        <p className="text-xs text-slate-500 mb-1.5">Invia a un contatto</p>
        <div className="flex flex-wrap gap-2">
          <a className={link + ' text-green-700 border-green-200'} href={`https://wa.me/?text=${enc(postText)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp">WhatsApp</a>
          <a className={link + ' text-sky-700 border-sky-200'} href={`https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`} target="_blank" rel="noreferrer" data-testid="share-telegram">Telegram</a>
          <a className={link + ' border-slate-300'} href={`mailto:?subject=${enc(title)}&body=${enc(text + '\n' + url)}`} data-testid="share-email"><Mail size={14} /> Email</a>
          <button type="button" className={link + ' border-slate-300'} onClick={() => copy('link', url)} data-testid="share-copy"><Copy size={14} /> {copied === 'link' ? 'Copiato!' : 'Copia link'}</button>
        </div>
      </div>
    </div>
  );
}

const OPERATIONAL = [
  { icon: User, label: 'Operatore specializzato' },
  { icon: Users, label: 'Capo Squadra' },
  { icon: ClipboardCheck, label: 'Preposto' },
  { icon: HardHat, label: 'Capo Cantiere' },
];
const CAREER = {
  'sabbiatore': OPERATIONAL,
  'verniciatore': OPERATIONAL,
  'contabile-cantiere': [
    { icon: User, label: 'Contabile di cantiere' },
    { icon: ClipboardCheck, label: 'Cost Controller' },
    { icon: Briefcase, label: 'Project Manager' },
  ],
  'aspp-operations': [
    { icon: Shield, label: 'ASPP' },
    { icon: ShieldCheck, label: 'Coordinatore HSE / RSPP' },
    { icon: Briefcase, label: 'HSE Manager' },
  ],
  'autista-patente-c-cqc': [
    { icon: User, label: 'Autista + supporto' },
    { icon: ClipboardCheck, label: 'Referente magazzino/logistica' },
    { icon: Briefcase, label: 'Coordinatore mezzi' },
  ],
  'geometra-ingegnere': [
    { icon: User, label: 'Tecnico di commessa' },
    { icon: ClipboardCheck, label: 'Responsabile ufficio tecnico' },
    { icon: Briefcase, label: 'Project Manager' },
  ],
  'operaio-generico': [
    { icon: User, label: 'Operaio generico' },
    { icon: TrendingUp, label: 'Sabbiatore / Verniciatore' },
    { icon: Briefcase, label: 'Operatore specializzato' },
  ],
};
const careerFor = (slug) => CAREER[slug] || OPERATIONAL;

function CareerPath({ slug }) {
  const steps = careerFor(slug);
  return (
    <div className="rounded-xl border border-[#1a4b8c]/20 bg-gradient-to-br from-blue-50 to-white p-4" data-testid="career-path">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={18} className="text-[#1a4b8c]" />
        <p className="font-semibold text-slate-800">Possibilità di crescita</p>
      </div>
      <p className="text-xs text-slate-500 mb-4">In Metal Covering il percorso è basato su merito ed esperienza: si cresce per step.</p>
      <div className="flex flex-wrap items-center gap-2">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="flex items-center gap-2">
              <div className="flex flex-col items-center text-center w-24">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${i === 0 ? 'bg-slate-200 text-slate-600' : 'bg-[#1a4b8c] text-white'}`}><Icon size={22} /></div>
                <span className="text-[11px] font-medium text-slate-700 mt-1 leading-tight">{s.label}</span>
              </div>
              {i < steps.length - 1 && <ChevronRight size={20} className="text-[#1a4b8c] shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL + '/api/public/recruitment';
const get = (u) => fetch(API + u).then(r => r.json());
const post = (u, b) => fetch(API + u, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) }).then(r => r.json());
const put = (u, b) => fetch(API + u, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) }).then(r => r.json());

const HERO_IMG = 'https://images.unsplash.com/photo-1681812508855-f7b04fefcd83?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const roleImage = (p) => ((p && p.seo && p.seo.og) || '/og/recruiting-general.jpg').replace('.png', '.jpg');

const HIGHLIGHTS = {
  'aspp-operations': ['ASPP operativo in campo: sopralluoghi, DPI, permessi di lavoro', 'Spazi confinati: Allegato 8, JLA, Toolbox e meeting pre-ingresso', 'Programma LPS SARPOM: Near Miss, LPO, riunioni', 'Magazzino (entrate/uscite, richieste offerta) + scadenziari DPI/estintori/rilevatori', 'Risorse umane: timbrature, presenze, ferie, scadenze attestati'],
  'sabbiatore': ['Preparazione superfici e sabbiatura di serbatoi, tubazioni, strutture', 'Uso sabbiatrice, ugelli, pressioni e gestione abrasivi', 'Gradi di preparazione Sa 1 / 2 / 2½ / 3', 'Lavori in quota e in spazi confinati', 'Controllo qualità del risultato e rispetto procedure HSE'],
  'verniciatore': ['Cicli di verniciatura industriale: primer, intermedi, finiture', 'Catalisi, miscelazione, pot life e diluizione', 'Applicazione a pennello, rullo e airless + stripe coat', 'Controllo spessori WFT/DFT e condizioni ambientali', 'Riconoscimento e riparazione difetti'],
  'contabile-cantiere': ['Controllo flussi economici di commessa (entrate/uscite)', 'Preparazione e aggiornamento SAL, quantità ed extra', 'Ordini di acquisto, PO e SES, verifica DDT e fatture', 'Cost control: confronto budget-costi-avanzamento', 'Excel avanzato e AutoCAD per lettura/misura'],
  'autista-patente-c-cqc': ['Trasporti con mezzi aziendali (camion fornito da Metal Covering)', 'Consegne in cantiere e ritiri fornitori; DDT e ancoraggio carico', 'Tachigrafo, tempi di guida/riposo, controlli pre-partenza', 'Supporto magazzino: picking, giacenze, entrate/uscite', 'Supporto manutenzione ordinaria e attività da operaio generico'],
  'geometra-ingegnere': ['Rilievi dimensionali e sopralluoghi in cantiere', 'Computi metrici e calcolo superfici (serbatoi, tubazioni, carpenterie)', 'AutoCAD/DWG: misure, tavole, planimetrie, export PDF', 'Preventivi tecnici e supporto ai SAL', 'Pianificazione, cronoprogrammi e supporto ai cantieri'],
  'operaio-generico': ['Supporto alle squadre in cantiere sotto la direzione del preposto', 'Supporto a sabbiatura e verniciatura (con formazione)', 'Preparazione aree, movimentazione materiali, pulizia industriale', 'Sicurezza: meeting pre-ingresso, Toolbox, DPI, permessi', 'Aperto anche a profili junior da formare'],
};
const CONDITIONS = [
  { icon: ClipboardCheck, t: 'Assunzione regolare CCNL', d: 'Inquadramento e livello commisurati a esperienza e competenze: più esperienza e attestati porti, più il tuo profilo cresce.' },
  { icon: MapPin, t: 'Zona di lavoro', d: 'Prediligiamo candidati vicini alle nostre sedi (SARPOM Trecate e ufficio Lainate). Valutiamo anche altre zone: per le trasferte autorizzate vitto e alloggio sono inclusi.' },
  { icon: Shield, t: 'Periodo di prova', d: 'Minimo 30 giorni (non una singola giornata): tempo reale per conoscerci. Formazione obbligatoria e visita medica sono previste fin dall\'inizio.' },
  { icon: TrendingUp, t: 'Programma Regards', d: 'Nessun benefit auto o buoni pasto: tutti partecipano al programma Regards, che trasforma i tuoi punti in vantaggi.' },
];

const CERT_LABELS = {
  formazione_generale: 'Formazione generale', formazione_specifica: 'Formazione specifica',
  spazi_confinati: 'Spazi confinati', spazi_confinati_triennale: 'Spazi confinati - esperienza triennale documentabile',
  h2s: 'H2S', so2: 'SO2', seveso: 'Seveso', primo_soccorso: 'Primo soccorso', antincendio: 'Antincendio',
  carrello_elevatore: 'Carrello elevatore (muletto)', ple: 'PLE (piattaforma elevabile)',
  lavori_in_quota: 'Lavori in quota', dpi_terza_categoria: 'DPI III categoria', preposto: 'Preposto',
  aspp_mod_a: 'ASPP Modulo A', aspp_mod_b: 'ASPP Modulo B',
};
const CERT_KEYS = Object.keys(CERT_LABELS);

export default function PublicRecruitmentPage() {
  const { token: routeToken, seoSlug } = useParams();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [step, setStep] = useState('list');
  const [position, setPosition] = useState(null);
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', phone: '', city: '', province: '', cap: '', license: '', has_car: false, work_status: '', availability_date: '' });
  const [session, setSession] = useState(null); // {token, application_id}
  const [answers, setAnswers] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [answersByPos, setAnswersByPos] = useState({});
  const [primaryId, setPrimaryId] = useState(null);
  const [consents, setConsents] = useState({ privacy: false, veridicita: false });
  const [saved, setSaved] = useState('');
  const [resumeUrl, setResumeUrl] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [certs, setCerts] = useState({});
  const [busy, setBusy] = useState(false);
  const [appMode, setAppMode] = useState('completa');
  const [tracking] = useState(() => captureTracking());
  const conversionFired = useRef(false);

  useEffect(() => {
    get('/positions').then(d => { setPositions(d.positions || []); setConditions(d.conditions || []); });
    post('/track', {}).catch(() => {});
  }, []);

  useEffect(() => {
    if (routeToken) {
      get(`/resume/${routeToken}`).then(d => {
        if (d.candidate) {
          setProfile(p => ({ ...p, ...d.candidate }));
          const draft = (d.applications || []).find(a => a.status === 'bozza') || d.applications?.[0];
          if (draft) {
            setSession({ token: routeToken, application_id: draft.id });
            setAnswers(draft.answers || {});
            get(`/positions/${draft.position_id}`).then(setPosition);
            setStep('questions');
          }
        }
      });
    }
  }, [routeToken]);

  useEffect(() => {
    if (seoSlug) {
      get(`/by-seo/${seoSlug}`).then(d => { if (d && d.id) { setPosition(d); setStep('detail'); } });
    }
  }, [seoSlug]);

  // Evento Google Ads: candidatura completata (una sola volta, alla conferma finale)
  useEffect(() => {
    if (step === 'done' && submitResult && submitResult.ok && !conversionFired.current) {
      conversionFired.current = true;
      fireApplicationCompleted();
    }
  }, [step, submitResult]);

  const openRole = (p) => { get(`/positions/${p.id}`).then(setPosition); setSelectedIds([p.id]); setPrimaryId(p.id); setStep('detail'); };
  const toggleSelect = (id) => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const startApplication = async (target = 'questions', mode = 'completa') => {
    if (!profile.email || !profile.first_name) { alert('Nome ed email obbligatori'); return; }
    const ids = selectedIds.length ? selectedIds : (position ? [position.id] : []);
    const primary = primaryId && ids.includes(primaryId) ? primaryId : ids[0];
    setAppMode(mode);
    setBusy(true);
    const r = await post('/start', { ...profile, position_ids: ids, primary_position_id: primary, tracking });
    setBusy(false);
    if (!r.token) { alert(r.detail || 'Errore'); return; }
    setSession({ token: r.token, application_id: r.application_id });
    setResumeUrl(window.location.origin + r.resume_url);
    setPrimaryId(primary);
    if (target === 'questions') {
      const full = await Promise.all(ids.map(id => get(`/positions/${id}`)));
      setSelectedPositions(full.filter(Boolean));
    }
    setStep(target);
  };

  const saveDraft = async () => {
    if (!session) return;
    const r = await put(`/applications/${session.token}/${session.application_id}`, { ...profile, answers: answersByPos[primaryId] || {}, consents, certificates: Object.entries(certs).filter(([, v]) => v.possessed).map(([type, v]) => ({ type, name: CERT_LABELS[type], ...v })) });
    if (r.ok) setSaved(new Date().toLocaleTimeString('it-IT'));
  };

  const uploadFile = async (kind, file) => {
    if (!file || !session) return;
    const fd = new FormData(); fd.append('file', file);
    await fetch(`${API}/applications/${session.token}/${session.application_id}/upload?kind=${kind}`, { method: 'POST', body: fd });
    setSaved('file caricato');
  };

  const submit = async () => {
    if (!consents.privacy || !consents.veridicita) { alert('Accetta i consensi obbligatori'); return; }
    setBusy(true);
    await saveDraft();
    const r = await post(`/applications/${session.token}/${session.application_id}/submit`, { answers_by_position: answersByPos, primary_position_id: primaryId, consents, mode: appMode });
    setBusy(false);
    if (r.ok) { setSubmitResult(r); setStep('done'); } else alert(r.detail || 'Compila i campi obbligatori');
  };

  const setAns = (id, v) => setAnswers(a => ({ ...a, [id]: v }));
  const setAnsPos = (pid, id, v) => setAnswersByPos(a => ({ ...a, [pid]: { ...(a[pid] || {}), [id]: v } }));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#1a4b8c] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center gap-3">
          <img src="/logo.svg" alt="Metal Covering" className="h-11 w-11 shrink-0 brightness-0 invert" data-testid="company-logo" />
          <div><h1 className="text-2xl font-bold">Lavora con noi – Metal Covering</h1><p className="text-sm text-blue-100">Campagna assunzioni · candidati in pochi passi</p></div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {step === 'list' && (
          <div className="space-y-6" data-testid="positions-list">
            <div className="relative rounded-2xl overflow-hidden">
              <img src={HERO_IMG} alt="Metal Covering" className="w-full h-56 md:h-72 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a4b8c]/90 to-[#1a4b8c]/40 flex flex-col justify-center p-6 md:p-10">
                <p className="text-blue-100 text-sm font-medium uppercase tracking-wide">Campagna Assunzioni</p>
                <h2 className="text-white text-2xl md:text-4xl font-extrabold leading-tight max-w-xl">Costruisci la tua carriera dove conta la sicurezza e il mestiere</h2>
                <p className="text-blue-50 mt-2 max-w-lg text-sm md:text-base">Entra in Metal Covering: formazione, crescita reale e lavoro in impianti industriali di tutta Italia.</p>
              </div>
            </div>
            <p className="text-slate-600 font-medium">Scegli la posizione per cui vuoi candidarti:</p>
            <div className="grid md:grid-cols-2 gap-4">
              {positions.map(p => (
                <Card key={p.id} className={`group overflow-hidden hover:shadow-lg transition-shadow ${p.waitlist ? 'border-2 border-amber-400' : 'border-slate-200'}`} data-testid={`position-${p.id}`}>
                  <div className="relative h-36 overflow-hidden">
                    <img src={roleImage(p)} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                    {p.waitlist && (
                      <div className="absolute top-0 inset-x-0 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 flex items-center gap-1.5 shadow" data-testid={`waitlist-badge-${p.id}`}>
                        <ClipboardCheck size={14} /> LISTA D'ATTESA · al momento non assumiamo
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4 space-y-2">
                    <h3 className="font-bold text-slate-800">{p.title}</h3>
                    <p className="text-xs text-slate-500">{p.subtitle}</p>
                    <p className="text-sm text-slate-600 flex items-center gap-1"><MapPin size={14} /> {p.location}</p>
                    <p className="text-sm text-slate-600">{p.activity}</p>
                    {p.waitlist
                      ? <Button className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => openRole(p)} data-testid={`open-role-${p.id}`}>Entra in lista d'attesa <ArrowRight size={15} className="ml-1" /></Button>
                      : <Button className="w-full mt-2" onClick={() => openRole(p)} data-testid={`open-role-${p.id}`}>Scopri il ruolo <ArrowRight size={15} className="ml-1" /></Button>}
                    <label className="flex items-center gap-2 text-xs text-slate-600 pt-1 cursor-pointer"><Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-${p.id}`} /> Aggiungi per candidarti a più posizioni</label>
                  </CardContent>
                </Card>
              ))}
            </div>
            {selectedIds.length > 0 && (
              <div className="sticky bottom-3 z-30 rounded-xl border-2 border-[#1a4b8c] bg-white shadow-lg p-3 flex items-center justify-between gap-3" data-testid="multi-select-bar">
                <span className="text-sm font-semibold text-slate-700">{selectedIds.length} posizioni selezionate</span>
                <Button onClick={() => { setPrimaryId(selectedIds[0]); setPosition(null); setStep('profile'); }} data-testid="multi-continue">Candidati per le posizioni selezionate <ArrowRight size={15} className="ml-1" /></Button>
              </div>
            )}
            <ShareBar
              url={window.location.origin + '/lavora-con-noi'}
              title="Metal Covering assume"
              text="Metal Covering S.r.l. sta cercando nuove figure professionali per Trecate, Lainate e i cantieri del Nord Italia. Scopri le posizioni e candidati:"
              label="Condividi le posizioni aperte"
              image="/og/recruiting-general.jpg"
            />
            <CompanyInfo />
          </div>
        )}

        {step === 'detail' && position && (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setStep('list')}><ArrowLeft size={15} className="mr-1" /> Tutte le posizioni</Button>
            <ShareBar
              url={window.location.origin + '/lavora-con-noi/' + (position.seo_slug || position.id)}
              title={'Metal Covering assume: ' + position.title}
              text={'Metal Covering cerca ' + position.title + '. Scopri il ruolo e candidati:'}
              label="Condividi questa posizione"
              image={((position.seo && position.seo.og) || '/og/recruiting-general.jpg').replace('.png', '.jpg')}
            />
            <div className="relative rounded-2xl overflow-hidden">
              <img src={roleImage(position)} alt={position.title} className="w-full h-48 md:h-64 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a4b8c]/95 via-[#1a4b8c]/40 to-transparent flex flex-col justify-end p-5">
                <h2 className="text-white text-2xl font-extrabold drop-shadow">{position.title}</h2>
                <p className="text-blue-100 text-sm">{position.subtitle}</p>
                <p className="text-blue-50 text-xs flex items-center gap-1 mt-1"><MapPin size={13} /> {position.location}</p>
              </div>
            </div>
            {position.waitlist && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="waitlist-notice">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0"><ClipboardCheck size={18} className="text-amber-700" /></div>
                <p className="text-sm text-amber-900"><b>Posizione attualmente chiusa — lista d'attesa.</b> Al momento non stiamo assumendo per questo ruolo, ma puoi comunque candidarti: entrerai nella nostra lista e sarai ricontattato appena si apre una posizione.</p>
              </div>
            )}
            <Card><CardContent className="p-5 space-y-4">
              <p className="text-slate-700 whitespace-pre-line">{position.description}</p>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Cosa farai</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {(HIGHLIGHTS[position.slug] || []).map((h, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-2" data-testid={`hl-${i}`}>
                      <CheckCircle2 size={16} className="text-[#1a4b8c] shrink-0 mt-0.5" /> {h}
                    </div>
                  ))}
                </div>
              </div>
              <RoleDetails slug={position.slug} />
              <CareerPath slug={position.slug} />
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Cosa offriamo</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {CONDITIONS.map((c, i) => { const Icon = c.icon; return (
                    <div key={i} className="flex items-start gap-3 border border-slate-100 rounded-lg p-3">
                      <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0"><Icon size={18} className="text-[#1a4b8c]" /></div>
                      <div><p className="font-medium text-sm text-slate-800">{c.t}</p><p className="text-xs text-slate-500">{c.d}</p></div>
                    </div>
                  ); })}
                </div>
              </div>
              {position.waitlist
                ? <Button size="lg" className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setStep('profile')} data-testid="apply-btn">Entra in lista d'attesa <ArrowRight size={16} className="ml-1" /></Button>
                : <Button size="lg" className="w-full" onClick={() => setStep('profile')} data-testid="apply-btn">Candidati ora <ArrowRight size={16} className="ml-1" /></Button>}
            </CardContent></Card>
          </div>
        )}

        {step === 'profile' && (
          <Card><CardContent className="p-5 space-y-3" data-testid="profile-form">
            <h2 className="text-lg font-bold">Crea il tuo profilo · {position?.title}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome *</Label><Input value={profile.first_name} onChange={e => setProfile({ ...profile, first_name: e.target.value })} data-testid="p-first" /></div>
              <div><Label>Cognome *</Label><Input value={profile.last_name} onChange={e => setProfile({ ...profile, last_name: e.target.value })} data-testid="p-last" /></div>
              <div><Label>Email *</Label><Input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} data-testid="p-email" /></div>
              <div><Label>Telefono</Label><Input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} /></div>
              <div><Label>Comune</Label><Input value={profile.city} onChange={e => setProfile({ ...profile, city: e.target.value })} /></div>
              <div><Label>Provincia</Label><Input value={profile.province} onChange={e => setProfile({ ...profile, province: e.target.value })} /></div>
              <div><Label>Patente</Label><Input value={profile.license} onChange={e => setProfile({ ...profile, license: e.target.value })} /></div>
              <div><Label>Disponibile dal</Label><Input type="date" value={profile.availability_date} onChange={e => setProfile({ ...profile, availability_date: e.target.value })} /></div>
              <div><Label>Situazione lavorativa</Label>
                <Select value={profile.work_status || undefined} onValueChange={v => setProfile({ ...profile, work_status: v })}>
                  <SelectTrigger data-testid="p-workstatus"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="libero">Libero da subito</SelectItem><SelectItem value="occupato">Attualmente occupato</SelectItem><SelectItem value="preavviso">In preavviso</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={profile.has_car} onCheckedChange={v => setProfile({ ...profile, has_car: !!v })} /> Automunito</label>
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-slate-600 space-y-1">
              <p className="font-semibold text-slate-800">Scegli come candidarti</p>
              <p><b>Candidatura completa</b>: rispondi al questionario e indica gli attestati. Non è obbligatorio rispondere a tutto, ma più informazioni fornisci, più sali in graduatoria.</p>
              <p><b>Candidatura semplice</b>: carichi solo CV e dati principali. Più rapida, ma verrai valutato di meno e in graduatoria avrai un punteggio più basso.</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep('detail')}><ArrowLeft size={15} className="mr-1" /> Indietro</Button>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => startApplication('documents', 'semplice')} disabled={busy} data-testid="start-simple-btn">Candidatura semplice (solo CV)</Button>
                <Button onClick={() => startApplication('questions', 'completa')} disabled={busy} data-testid="start-btn">Candidatura completa <ArrowRight size={15} className="ml-1" /></Button>
              </div>
            </div>
          </CardContent></Card>
        )}

        {step === 'questions' && selectedPositions.length > 0 && (
          <div className="space-y-4" data-testid="questions-step">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Questionari ({selectedPositions.length} {selectedPositions.length > 1 ? 'posizioni' : 'posizione'})</h2>
              <div className="text-xs text-slate-500 flex items-center gap-2">{saved && <span>Salvato {saved}</span>}<Button size="sm" variant="outline" onClick={saveDraft} data-testid="save-draft"><Save size={14} className="mr-1" /> Salva bozza</Button></div>
            </div>
            {resumeUrl && <Card className="bg-amber-50 border-amber-200"><CardContent className="p-3 text-xs text-amber-800">Puoi riprendere questa candidatura da: <a className="underline break-all" href={resumeUrl}>{resumeUrl}</a> (inviato anche via email).</CardContent></Card>}
            {selectedPositions.length > 1 && (
              <Card><CardContent className="p-4 space-y-2" data-testid="primary-choice">
                <p className="text-sm font-semibold text-slate-800">Qual è la posizione che preferisci?</p>
                <div className="flex flex-wrap gap-2">
                  {selectedPositions.map(sp => (
                    <label key={sp.id} className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-1.5 cursor-pointer ${primaryId === sp.id ? 'border-[#1a4b8c] bg-blue-50' : 'border-slate-200'}`}>
                      <input type="radio" name="primary" checked={primaryId === sp.id} onChange={() => setPrimaryId(sp.id)} data-testid={`primary-${sp.id}`} /> {sp.title}
                    </label>
                  ))}
                </div>
              </CardContent></Card>
            )}
            <Card className="bg-blue-50 border-blue-100"><CardContent className="p-3 text-xs text-slate-600" data-testid="questions-note">Nessuna risposta è obbligatoria: puoi rispondere anche "no" o lasciare vuoto. Le risposte servono a costruire una graduatoria separata per ogni ruolo scelto — più informazioni fornisci, più cresce la tua compatibilità.</CardContent></Card>
            {selectedPositions.map((sp, idx) => { const av = answersByPos[sp.id] || {}; return (
              <Card key={sp.id} data-testid={`questionnaire-${sp.id}`}><CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-slate-800">{sp.title}</h3>
                  <span className="text-xs text-slate-500">Questionario {idx + 1} di {selectedPositions.length}</span>
                </div>
                {(sp.questions || []).map(q => (
                  <div key={q.id} className="space-y-1" data-testid={`q-${sp.id}-${q.id}`}>
                    <Label>{q.text} {q.eliminatory && <Badge variant="outline" className="text-[#1a4b8c] border-blue-200 text-[10px]">chiave per la graduatoria</Badge>}</Label>
                    {q.type === 'boolean' ? (
                      <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!av[q.id]} onCheckedChange={v => setAnsPos(sp.id, q.id, !!v)} /> Sì</label>
                    ) : q.type === 'scale' ? (
                      <Input type="number" min="1" max="5" value={av[q.id] || ''} onChange={e => setAnsPos(sp.id, q.id, e.target.value)} />
                    ) : q.type === 'number' ? (
                      <Input type="number" value={av[q.id] || ''} onChange={e => setAnsPos(sp.id, q.id, e.target.value)} />
                    ) : q.type === 'multiple' ? (
                      <div className="flex flex-wrap gap-3">{(q.options || []).map(o => { const sel = (av[q.id] || []).includes(o); return <label key={o} className="flex items-center gap-1 text-sm"><Checkbox checked={sel} onCheckedChange={v => { const cur = av[q.id] || []; setAnsPos(sp.id, q.id, v ? [...cur, o] : cur.filter(x => x !== o)); }} /> {o}</label>; })}</div>
                    ) : (
                      <Textarea rows={2} value={av[q.id] || ''} onChange={e => setAnsPos(sp.id, q.id, e.target.value)} />
                    )}
                  </div>
                ))}
              </CardContent></Card>
            ); })}
            <Card><CardContent className="p-5 space-y-3" data-testid="certs-section">
              <h3 className="font-semibold text-slate-800">Attestati e corsi</h3>
              <p className="text-xs text-slate-500">Indica gli attestati posseduti con data, scadenza ed ente. Potrai allegare i documenti nel passo successivo.</p>
              <div className="space-y-2">
                {CERT_KEYS.map(k => { const cv = certs[k] || {}; return (
                  <div key={k} className="border border-slate-100 rounded-lg p-2" data-testid={`cert-${k}`}>
                    <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={!!cv.possessed} onCheckedChange={v => setCerts(c => ({ ...c, [k]: { ...c[k], possessed: !!v } }))} /> {CERT_LABELS[k]}</label>
                    {cv.possessed && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        <div><Label className="text-xs">Conseguito</Label><Input type="date" value={cv.issue_date || ''} onChange={e => setCerts(c => ({ ...c, [k]: { ...c[k], issue_date: e.target.value } }))} /></div>
                        <div><Label className="text-xs">Scadenza</Label><Input type="date" value={cv.expiry_date || ''} onChange={e => setCerts(c => ({ ...c, [k]: { ...c[k], expiry_date: e.target.value } }))} /></div>
                        <div><Label className="text-xs">Ente</Label><Input value={cv.entity || ''} onChange={e => setCerts(c => ({ ...c, [k]: { ...c[k], entity: e.target.value } }))} /></div>
                        <div><Label className="text-xs">N. attestato</Label><Input value={cv.number || ''} onChange={e => setCerts(c => ({ ...c, [k]: { ...c[k], number: e.target.value } }))} /></div>
                      </div>
                    )}
                  </div>
                ); })}
              </div>
              <div><Label>Conoscenze / specializzazioni speciali</Label><Textarea rows={2} value={answers.q_special || ''} onChange={e => setAns('q_special', e.target.value)} placeholder="Es. patentini particolari, macchinari, esperienze specifiche..." data-testid="special-skills" /></div>
            </CardContent></Card>
            <div className="flex justify-end"><Button onClick={() => setStep('documents')} data-testid="to-documents">Continua <ArrowRight size={15} className="ml-1" /></Button></div>
          </div>
        )}

        {step === 'documents' && (
          <Card><CardContent className="p-5 space-y-4" data-testid="documents-step">
            <h2 className="text-lg font-bold">Documenti e consensi</h2>
            {appMode === 'semplice' && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="simple-banner">
                <span>Stai inviando una <b>candidatura semplice</b>: sarai valutato solo su CV e dati principali, con punteggio più basso in graduatoria.</span>
                <Button size="sm" variant="outline" onClick={() => { setAppMode('completa'); setStep('questions'); }} data-testid="upgrade-to-full">Rispondi al questionario</Button>
              </div>
            )}
            <div><Label>CV (PDF/DOC)</Label><Input type="file" accept=".pdf,.doc,.docx" onChange={e => uploadFile('cv', e.target.files?.[0])} data-testid="cv-input" /></div>
            <div><Label>Fotografia (facoltativa)</Label><Input type="file" accept="image/*" onChange={e => uploadFile('photo', e.target.files?.[0])} /></div>
            <div><Label>Altri documenti / attestati</Label><Input type="file" onChange={e => uploadFile('attestato', e.target.files?.[0])} /></div>
            <div className="space-y-2 pt-2 border-t">
              <label className="flex items-start gap-2 text-sm"><Checkbox checked={consents.privacy} onCheckedChange={v => setConsents({ ...consents, privacy: !!v })} data-testid="consent-privacy" /> <span>Ho letto l'<a href="/lavora-con-noi/privacy" target="_blank" rel="noreferrer" className="text-[#1a4b8c] underline">Informativa Privacy</a> e acconsento al trattamento dei dati per la selezione.</span></label>
              <label className="flex items-start gap-2 text-sm"><Checkbox checked={consents.veridicita} onCheckedChange={v => setConsents({ ...consents, veridicita: !!v })} data-testid="consent-truth" /> Dichiaro che le informazioni fornite sono veritiere.</label>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(appMode === 'semplice' ? 'profile' : 'questions')}><ArrowLeft size={15} className="mr-1" /> Indietro</Button>
              <Button onClick={submit} disabled={busy} data-testid="submit-btn">Invia candidatura</Button>
            </div>
          </CardContent></Card>
        )}

        {step === 'done' && (
          <Card><CardContent className="p-8 text-center space-y-3" data-testid="done-step">
            <CheckCircle2 size={48} className="mx-auto text-green-600" />
            <h2 className="text-xl font-bold">Candidatura inviata!</h2>
            {submitResult && (() => {
              const c = submitResult.classe;
              const strong = ['A', 'B'].includes(c);
              const good = c === 'C';
              const msg = strong ? 'Il tuo profilo è molto in linea con la posizione: ottime possibilità di proseguire.'
                : good ? 'Il tuo profilo è interessante: buone possibilità di essere ricontattato.'
                : 'Grazie: valuteremo con attenzione il tuo profilo.';
              return (
                <div className="max-w-md mx-auto space-y-2">
                  <div className={`inline-block px-4 py-2 rounded-full font-semibold ${strong ? 'bg-green-100 text-green-700' : good ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-testid="compat-badge">
                    Compatibilità: {submitResult.total}/100
                  </div>
                  <p className="text-slate-600">{msg}</p>
                  {submitResult.positions && submitResult.positions.length > 1 && (
                    <div className="mt-3 space-y-1.5 text-left" data-testid="per-position-results">
                      <p className="text-xs font-semibold text-slate-500 text-center">Compatibilità per ogni posizione scelta</p>
                      {submitResult.positions.map((pp, i) => (
                        <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`result-pos-${i}`}>
                          <span className="text-slate-700">{pp.title}</span>
                          <span className="font-semibold text-slate-800">{pp.total}/100 · Classe {pp.classe}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-400">Più esperienza, attestati e competenze aggiungi al tuo profilo, più cresce la tua compatibilità — e con essa il livello di inquadramento riconosciuto.</p>
                </div>
              );
            })()}
            <p className="text-slate-600">Ti contatteremo per i prossimi passi (video-call e prova pratica). Riceverai una email di conferma.</p>
            <Button variant="outline" onClick={() => { setStep('list'); setSession(null); setAnswers({}); setSubmitResult(null); }}>Torna alle posizioni</Button>
          </CardContent></Card>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-white mt-8" data-testid="public-footer">
        <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Metal Covering S.r.l. · P.IVA 02932950799 · Vibo Marina (VV)</span>
          <div className="flex items-center gap-4">
            <a href="/lavora-con-noi/privacy" className="hover:text-[#1a4b8c] underline" data-testid="footer-privacy">Informativa Privacy &amp; Cookie</a>
            <button onClick={openCookiePreferences} className="hover:text-[#1a4b8c] underline" data-testid="footer-cookie-prefs">Preferenze cookie</button>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
}
